'use client'

import { useState, useEffect } from 'react'
import { createPortal } from 'react-dom'
import Link from 'next/link'
import { useRouter } from 'next/navigation'
import Editor from '@monaco-editor/react'
import { analyzeCodeForAI } from '@/lib/aiDetector'
import UserName from '@/components/UserName'

// --- PORTAL-BASED HOSTING MODAL WITH DEPLOY ROUTING ---
function HostProjectModal({ script, storeItems = [] }: { script: any; storeItems: any[] }) {
  const [isOpen, setIsOpen] = useState(false)
  const [mounted, setMounted] = useState(false)

  useEffect(() => {
    setMounted(true)
  }, [])

  const lang = script.language.toLowerCase()
  const isProject = script.itemType === 'PROJECT' || (script.files && script.files.length > 0)
  const requiresVPS = lang.includes('node') || lang.includes('python') || lang.includes('docker') || lang.includes('rust') || lang.includes('go') || isProject

  const matchingStoreItems = storeItems.filter(item => {
    const name = item.name.toLowerCase()
    if (requiresVPS) {
      return name.includes('vps') || name.includes('dedicated') || name.includes('node') || name.includes('server')
    } else {
      return name.includes('web') || name.includes('shared') || name.includes('hosting')
    }
  })

  const displayItems = matchingStoreItems.length > 0 ? matchingStoreItems : storeItems

  const modalContent = isOpen ? (
    <div className="fixed inset-0 z-[99999] flex items-center justify-center p-4 bg-black/85 backdrop-blur-md font-mono animate-fade-in">
      <div className="relative w-full max-w-xl bg-[#070b14] border-2 border-emerald-500/50 rounded-2xl p-6 sm:p-8 shadow-[0_0_80px_rgba(16,185,129,0.3)] space-y-6 max-h-[90vh] overflow-y-auto">
        
        <div className="flex items-center justify-between border-b border-slate-800 pb-4">
          <div>
            <div className="text-[10px] text-emerald-400 uppercase tracking-widest font-bold">Store Infrastructure Matcher</div>
            <h3 className="text-lg font-black text-white tracking-tight">Deploy &ldquo;{script.title}&rdquo;</h3>
          </div>
          <button 
            onClick={() => setIsOpen(false)} 
            className="w-8 h-8 rounded-lg bg-slate-900 text-slate-400 hover:text-white flex items-center justify-center border border-slate-800 cursor-pointer"
          >
            ✕
          </button>
        </div>

        <div className="space-y-4 text-xs">
          <div className="p-4 bg-slate-950 border border-slate-800 rounded-xl space-y-2">
            <div className="text-[10px] text-indigo-400 uppercase tracking-widest font-bold">Architecture Requirement</div>
            <div className="text-white font-bold text-sm">
              {requiresVPS ? '⚡ Requires VPS / Dedicated Execution Environment' : '🌐 Compatible with Standard Web Hosting'}
            </div>
            <p className="text-slate-400 text-xs font-sans">
              Target Language: <span className="text-indigo-300 uppercase font-mono">{script.language}</span> &bull; Type: <span className="text-emerald-300 font-mono">{isProject ? 'Multi-File Project' : 'Single Snippet'}</span>
            </p>
          </div>

          <div className="space-y-3">
            <div className="text-[10px] text-slate-400 uppercase tracking-widest font-bold">Available Store Packages:</div>
            
            {displayItems.length === 0 ? (
              <div className="p-4 bg-slate-950 border border-slate-800 rounded-xl text-center text-slate-500">
                No hosting packages found in the Points Store. Visit the admin panel or store manager to add some!
              </div>
            ) : (
              displayItems.map((item: any) => (
                <div key={item.id} className="p-4 bg-slate-950 border border-emerald-500/30 rounded-xl flex items-center justify-between gap-4 hover:border-emerald-500/60 transition-all">
                  <div className="space-y-1">
                    <div className="text-white font-bold text-sm">{item.name}</div>
                    <div className="text-slate-400 text-[11px] font-sans">{item.description || 'Optimized runtime environment.'}</div>
                  </div>
                  <div className="shrink-0 flex flex-col items-end gap-2">
                    <span className="text-emerald-400 font-bold bg-emerald-500/10 px-3 py-1 rounded border border-emerald-500/20 text-xs">
                      {item.priceInPoints} Pts
                    </span>
                    <Link 
                      href={`/vault/${script.id}/deploy?itemId=${item.id}`}
                      className="bg-emerald-600 hover:bg-emerald-500 text-white font-bold px-3 py-1.5 rounded-lg text-[10px] uppercase tracking-wider transition-all shadow-md"
                    >
                      Claim &amp; Deploy
                    </Link>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        <div className="flex justify-end pt-2 border-t border-slate-800">
          <button 
            onClick={() => setIsOpen(false)}
            className="bg-slate-900 hover:bg-slate-800 text-slate-300 border border-slate-800 font-bold px-6 py-2 rounded-xl text-xs transition-all cursor-pointer"
          >
            Close Window
          </button>
        </div>

      </div>
    </div>
  ) : null

  return (
    <>
      <button 
        onClick={() => setIsOpen(true)}
        className="bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white font-mono font-bold text-xs uppercase tracking-widest px-4 py-2 rounded-xl transition-all shadow-[0_0_15px_rgba(16,185,129,0.3)] cursor-pointer flex items-center gap-1.5"
      >
        <span>🚀</span> Host This Project
      </button>

      {mounted && isOpen && createPortal(modalContent, document.body)}
    </>
  )
}

export default function ScriptDetailClient({ script: initialScript, currentUserId, storeItems = [] }: { script: any; currentUserId?: string; storeItems?: any[] }) {
  const router = useRouter()
  const [script, setScript] = useState(initialScript)
  
  // --- VIRTUAL FILE SYSTEM DATA NORMALIZATION ---
  const isProject = script.itemType === 'PROJECT' || (script.files && script.files.length > 0)
  const displayFiles = isProject && script.files?.length > 0 
    ? script.files 
    : [{ id: 'legacy', path: `main.${script.language}`, content: script.code || '', language: script.language }]

  // --- IDE SORTING LOGIC ---
  const sortedFiles = [...displayFiles].sort((a, b) => {
    const aParts = a.path.split('/')
    const bParts = b.path.split('/')
    for (let i = 0; i < Math.min(aParts.length, bParts.length); i++) {
      if (aParts[i] !== bParts[i]) {
        const aIsFolder = i < aParts.length - 1 || aParts[i] === '.keep'
        const bIsFolder = i < bParts.length - 1 || bParts[i] === '.keep'
        if (aIsFolder && !bIsFolder) return -1
        if (!aIsFolder && bIsFolder) return 1
        return aParts[i].localeCompare(bParts[i])
      }
    }
    return aParts.length - bParts.length
  })

  const initialFile = sortedFiles.find(f => !f.path.endsWith('.keep')) || sortedFiles[0]
  const [activeFile, setActiveFile] = useState(initialFile)

  // --- UI STATES ---
  const [commentContent, setCommentContent] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [copied, setCopied] = useState(false)
  const [cliCopied, setCliCopied] = useState(false)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [activeTab, setActiveTab] = useState<'editor' | 'diff' | 'history' | 'linters'>('editor')
  const [isForking, setIsForking] = useState(false)

  const [selectedRevisionId, setSelectedRevisionId] = useState<string>(
    script.revisions?.[0]?.id || ''
  )

  const score = script.votes.reduce((acc: number, v: any) => acc + v.value, 0)
  
  const audit = analyzeCodeForAI(activeFile.content, script.description || '')
  const aiScore = script.aiScore ?? audit.aiScore
  const humanCraftScore = 100 - aiScore
  const breakdown = audit.breakdown

  const totalProjectLines = isProject 
    ? displayFiles.reduce((acc: number, f: any) => acc + (f.content ? f.content.split('\n').length : 0), 0)
    : (activeFile.content ? activeFile.content.split('\n').length : 0)
  const commitHash = btoa(script.id + script.title).slice(0, 10).toLowerCase()

  // --- MULTI-FILE DIFF RESOLUTION ---
  const selectedRev = script.revisions?.find((r: any) => r.id === selectedRevisionId) || script.revisions?.[0]
  let historicalFiles: any[] = []
  try {
    if (selectedRev?.files) {
      historicalFiles = JSON.parse(selectedRev.files)
    }
  } catch (e) {
    historicalFiles = []
  }

  const historicalFileMatch = historicalFiles.find((f: any) => f.path === activeFile.path)
  const historicalBaselineCode = historicalFileMatch 
    ? historicalFileMatch.content 
    : (selectedRev?.code || '// File did not exist in this revision snapshot')

  const handleCopy = () => {
    navigator.clipboard.writeText(activeFile.content)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  const handleCliCopy = (cmd: string) => {
    navigator.clipboard.writeText(cmd)
    setCliCopied(true)
    setTimeout(() => setCliCopied(false), 2000)
  }

  const handleFork = () => {
    setIsForking(true)
    setTimeout(() => {
      router.push(`/vault/new?fork=${script.id}&lang=${activeFile.language}&title=${encodeURIComponent(script.title + ' (Branch)')}&code=${encodeURIComponent(activeFile.content)}`)
    }, 400)
  }

  const handleVote = async (value: number) => {
    try {
      const res = await fetch(`/api/vault/${script.id}/vote`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ value })
      })
      const data = await res.json()
      if (res.ok) setScript((prev: any) => ({ ...prev, votes: data.votes }))
    } catch (e) {
      console.error(e)
    }
  }

  const handleComment = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!commentContent.trim()) return
    setSubmitting(true)
    try {
      const res = await fetch(`/api/vault/${script.id}/comment`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ content: commentContent })
      })
      const data = await res.json()
      if (res.ok) {
        setCommentContent('')
        setScript((prev: any) => ({ ...prev, comments: data.comments }))
      }
    } catch (e) {
      console.error(e)
    } finally {
      setSubmitting(false)
    }
  }

  const getMonacoLanguage = (lang: string) => {
    if (lang === 'bash') return 'shell'
    if (lang === 'docker') return 'dockerfile'
    if (lang === 'nginx') return 'shell'
    if (lang === 'sql') return 'sql'
    return lang
  }

  return (
    <div className="space-y-6 font-sans relative">
      
      {/* Top Navigation & Actions Bar */}
      <div className="flex flex-wrap items-center justify-between gap-4 bg-slate-900/65 backdrop-blur-2xl border border-slate-800/80 px-6 py-4 rounded-xl shadow-xl font-mono text-xs">
        <Link href="/vault" className="text-slate-400 hover:text-white transition-colors flex items-center gap-2 font-bold uppercase tracking-wider">
          &larr; Vault Command
        </Link>
        <div className="flex flex-wrap items-center gap-3">
          <button
            onClick={handleFork}
            disabled={isForking}
            className="bg-indigo-600/30 hover:bg-indigo-600/50 text-indigo-300 border border-indigo-500/40 px-4 py-2 rounded-lg transition-all flex items-center gap-1.5 font-bold cursor-pointer shadow-sm"
          >
            <span>🔀</span> {isForking ? 'Branching Workspace...' : 'Branch Payload'}
          </button>
          
          {/* UPDATED: Dynamic Routing for Sandbox based on item type */}
          {isProject ? (
            <Link 
              href={`/practice/${script.id}`}
              className="bg-indigo-600 hover:bg-indigo-500 text-white border border-indigo-500/40 px-4 py-2 rounded-lg transition-all flex items-center gap-1.5 font-bold cursor-pointer shadow-[0_0_15px_rgba(79,70,229,0.3)]"
            >
              <span>⚡</span> Open Sandbox
            </Link>
          ) : (
            <Link 
              href={`/vault/playground?lang=${activeFile.language}&code=${encodeURIComponent(activeFile.content)}`}
              className="bg-slate-800/80 hover:bg-slate-700 text-indigo-300 border border-indigo-500/30 px-4 py-2 rounded-lg transition-all flex items-center gap-1.5 font-bold cursor-pointer shadow-sm"
            >
              <span>⚡</span> Debug
            </Link>
          )}
          
          <a 
            href={`/api/vault/${script.id}/download`} 
            className="bg-slate-800/80 hover:bg-slate-700 text-indigo-300 border border-indigo-500/30 px-4 py-2 rounded-lg transition-all font-bold flex items-center gap-1.5 cursor-pointer shadow-sm"
          >
            <span>📦</span> Download .zip
          </a>

          {/* Portal-rendered Pop-up Modal Component */}
          <HostProjectModal script={script} storeItems={storeItems} />

          <a 
            href={`/api/vault/${script.id}/raw`} 
            target="_blank"
            className="bg-slate-800/80 hover:bg-slate-700 text-slate-300 border border-slate-700 px-4 py-2 rounded-lg transition-all font-bold flex items-center gap-1.5 cursor-pointer shadow-sm"
          >
            <span>↓</span> Raw Config
          </a>
        </div>
      </div>

      {/* Hero Header Card */}
      <article className="relative bg-gradient-to-br from-slate-900/80 via-[#070b14]/90 to-slate-950/90 backdrop-blur-2xl border border-slate-800/90 rounded-xl p-8 sm:p-10 shadow-2xl space-y-6 overflow-hidden">
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-gradient-to-br from-indigo-500/15 via-purple-500/10 to-transparent rounded-full blur-3xl pointer-events-none"></div>

        <div className="flex flex-wrap items-start justify-between gap-6 relative z-10">
          <div className="space-y-3 max-w-2xl">
            <div className="flex flex-wrap items-center gap-2.5 font-mono text-xs">
              <span className={`px-3 py-1 text-xs font-bold uppercase tracking-widest rounded-md border shadow-sm ${isProject ? 'bg-emerald-950/90 text-emerald-300 border-emerald-500/40' : 'bg-indigo-950/90 text-indigo-300 border-indigo-500/40'}`}>
                {isProject ? '📁 Project Payload' : '📄 Snippet'}
              </span>
              <span className="text-slate-600">•</span>
              <span className="text-slate-400">{script.language}</span>
            </div>
            <h1 className="text-2xl sm:text-4xl font-extrabold text-white tracking-tight">{script.title}</h1>
            {script.description && (
              <p className="text-slate-300 text-xs sm:text-sm leading-relaxed font-sans">
                {script.description}
              </p>
            )}

           

          </div>

          <div className="flex items-center gap-3 bg-slate-950/90 border border-slate-800 px-4 py-2 rounded-xl font-mono text-xs shadow-inner">
            <button onClick={() => handleVote(1)} className="text-slate-400 hover:text-emerald-400 transition-colors cursor-pointer font-bold px-1">▲</button>
            <span className={score >= 0 ? 'text-emerald-400 font-black text-sm px-1' : 'text-rose-400 font-black text-sm px-1'}>{score}</span>
            <button onClick={() => handleVote(-1)} className="text-slate-400 hover:text-rose-400 transition-colors cursor-pointer font-bold px-1">▼</button>
          </div>
        </div>

        <div className="flex flex-wrap items-center justify-between pt-5 border-t border-slate-800/80 text-xs text-slate-400 font-mono relative z-10">
          <div className="flex items-center gap-3">
            <div className="w-7 h-7 rounded-lg bg-indigo-950 border border-indigo-500/40 flex items-center justify-center text-xs font-bold text-indigo-300 shadow-inner overflow-hidden">
              {script.author?.image ? <img src={script.author.image} alt="" className="w-full h-full object-cover" /> : (script.author?.name || 'U')[0].toUpperCase()}
            </div>
            <span>Author:</span>
            <UserName userId={script.author.id} name={script.author.username || script.author.name} className="text-indigo-400 font-bold hover:underline" />
          </div>
          <div>Published {new Date(script.createdAt).toLocaleDateString()}</div>
        </div>
      </article>

      {/* CLI Widget */}
      <div className="bg-slate-900/65 backdrop-blur-2xl border border-indigo-500/30 rounded-xl p-5 shadow-xl font-mono text-xs flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div className="space-y-1">
          <div className="text-[10px] text-indigo-400 uppercase tracking-widest font-bold flex items-center gap-1.5">
            <span>⚡ DevYield CLI Terminal Bridge</span>
            <span className="px-1.5 py-0.5 bg-indigo-950 text-indigo-300 rounded text-[9px]">v1.2.0</span>
          </div>
          <div className="text-slate-300 font-mono text-xs">
            $ <span className="text-emerald-400">npx devyield pull {script.id}</span>
          </div>
        </div>
        <button
          onClick={() => handleCliCopy(`npx devyield pull ${script.id}`)}
          className="bg-indigo-600 hover:bg-indigo-500 text-white font-bold px-4 py-2 rounded-lg text-xs transition-all shadow-md cursor-pointer whitespace-nowrap"
        >
          {cliCopied ? '✓ CLI Command Copied!' : 'Copy CLI Pull Command'}
        </button>
      </div>

      {/* Bento Telemetry Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 font-mono">
        <div 
          onClick={() => setIsModalOpen(true)}
          className="bg-slate-900/60 backdrop-blur-xl border border-slate-800/80 hover:border-indigo-500/60 rounded-xl p-5 shadow-xl flex items-center justify-between cursor-pointer transition-all group relative overflow-hidden"
        >
          <div>
            <div className="text-[10px] text-slate-500 uppercase tracking-widest font-bold">Human Craftsmanship</div>
            <div className={`text-lg font-black mt-1 ${humanCraftScore >= 70 ? 'text-emerald-400' : 'text-amber-400'}`}>
              {humanCraftScore}% Verified
            </div>
            <div className="text-[10px] text-indigo-400 font-mono mt-0.5 group-hover:underline">
              Click to find out more &rarr;
            </div>
          </div>
          <div className="w-10 h-10 rounded-xl bg-emerald-950/80 border border-emerald-500/30 flex items-center justify-center text-emerald-400 text-sm shadow-inner group-hover:scale-110 transition-transform">
            🛡️
          </div>
        </div>

        <div className="bg-slate-900/60 backdrop-blur-xl border border-slate-800/80 rounded-xl p-5 shadow-xl flex items-center justify-between">
          <div>
            <div className="text-[10px] text-slate-500 uppercase tracking-widest font-bold">
              {isProject ? 'Project Dimensions' : 'Code Dimensions'}
            </div>
            <div className="text-sm font-black text-indigo-300 mt-1 flex flex-wrap gap-x-2">
              {isProject && <span>{displayFiles.length} Files &bull;</span>}
              <span>{totalProjectLines} Total LOC</span>
            </div>
          </div>
          <div className="w-10 h-10 rounded-xl bg-indigo-950/80 border border-indigo-500/30 flex items-center justify-center text-indigo-400 text-sm shadow-inner">
            📏
          </div>
        </div>

        <div className="bg-slate-900/60 backdrop-blur-xl border border-slate-800/80 rounded-xl p-5 shadow-xl flex items-center justify-between">
          <div>
            <div className="text-[10px] text-slate-500 uppercase tracking-widest font-bold">Community Activity</div>
            <div className="text-lg font-black text-purple-300 mt-1">
              {script.comments.length} Comments
            </div>
          </div>
          <div className="w-10 h-10 rounded-xl bg-purple-950/80 border border-purple-500/30 flex items-center justify-center text-purple-400 text-sm shadow-inner">
            💬
          </div>
        </div>
      </div>

      {/* 🚀 SPLIT-PANE IDE WORKSPACE */}
      <div className="grid grid-cols-1 md:grid-cols-12 gap-0 border border-slate-800 rounded-xl overflow-hidden shadow-2xl bg-[#020408] h-[650px]">
        
        {/* Left Sidebar: Virtual File Tree (Only visible if it's a project) */}
        {isProject && (
          <div className="md:col-span-3 border-b md:border-b-0 md:border-r border-slate-800 bg-[#050505] flex flex-col shrink-0">
            <div className="p-3 border-b border-slate-800 bg-[#0a0a0a] flex items-center justify-between">
              <span className="text-[10px] font-mono font-bold text-slate-400 uppercase tracking-widest">EXPLORER</span>
              <span className="text-[10px] font-mono text-slate-600">{displayFiles.length} files</span>
            </div>
            <div className="flex-1 overflow-y-auto p-2 space-y-0.5">
              {sortedFiles.map((f: any) => {
                const parts = f.path.split('/')
                const filename = parts.pop()
                const folder = parts.length > 0 ? parts.join('/') + '/' : ''
                const isKeepFile = filename === '.keep'

                return (
                  <div 
                    key={f.id} 
                    onClick={() => { if (!isKeepFile) setActiveFile(f) }} 
                    className={`flex items-center p-1.5 px-2 rounded-md cursor-pointer text-xs font-mono transition-colors group ${activeFile.id === f.id ? 'bg-indigo-600/20 text-indigo-300 border border-indigo-500/30' : 'text-slate-400 hover:bg-slate-900 border border-transparent'}`}
                    title={f.path}
                  >
                    <div className="truncate flex-1 flex items-center">
                      <span className="opacity-40 mr-1.5 text-[10px] shrink-0">
                        {isKeepFile ? '📁' : '📄'}
                      </span>
                      <span className="truncate">
                        <span className="text-slate-500">{folder}</span>
                        <span className={isKeepFile ? 'text-slate-600 italic' : 'text-slate-200'}>{filename}</span>
                      </span>
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        )}

        {/* Right Editor/Tabs Pane */}
        <div className={`${isProject ? 'md:col-span-9' : 'md:col-span-12'} flex flex-col bg-[#0a0a0a] overflow-hidden`}>
          
          {/* Workspace Tabs Header */}
          <div className="bg-slate-950 px-5 py-3 border-b border-slate-800 flex flex-wrap items-center justify-between gap-4 font-mono text-xs shrink-0">
            <div className="flex items-center gap-1.5 sm:gap-2">
              <button onClick={() => setActiveTab('editor')} className={`px-3 py-1.5 rounded-lg transition-all cursor-pointer font-bold whitespace-nowrap ${activeTab === 'editor' ? 'bg-indigo-600 text-white shadow-md' : 'text-slate-400 hover:text-white'}`}>💻 Editor</button>
              <button onClick={() => setActiveTab('diff')} className={`px-3 py-1.5 rounded-lg transition-all cursor-pointer font-bold whitespace-nowrap ${activeTab === 'diff' ? 'bg-indigo-600 text-white shadow-md' : 'text-slate-400 hover:text-white'}`}>🔀 Diff Viewer</button>
              <button onClick={() => setActiveTab('history')} className={`px-3 py-1.5 rounded-lg transition-all cursor-pointer font-bold whitespace-nowrap hidden sm:block ${activeTab === 'history' ? 'bg-indigo-600 text-white shadow-md' : 'text-slate-400 hover:text-white'}`}>📜 History</button>
              <button onClick={() => setActiveTab('linters')} className={`px-3 py-1.5 rounded-lg transition-all cursor-pointer font-bold whitespace-nowrap hidden sm:block ${activeTab === 'linters' ? 'bg-indigo-600 text-white shadow-md' : 'text-slate-400 hover:text-white'}`}>🛡️ Linters</button>
            </div>

            {activeTab === 'editor' && (
              <button onClick={handleCopy} className="bg-slate-800 hover:bg-slate-700 text-slate-200 border border-slate-700 font-bold px-4 py-1.5 rounded-lg text-[10px] sm:text-xs transition-all shadow-md cursor-pointer whitespace-nowrap">
                {copied ? '✓ Copied!' : 'Copy Code'}
              </button>
            )}
          </div>

          {/* Tab 1: Monaco Editor */}
          {activeTab === 'editor' && (
            <div className="flex-1 flex flex-col min-h-0">
              <div className="flex items-center justify-between p-2 sm:p-3 border-b border-slate-800 bg-[#050505] shrink-0">
                <div className="flex items-center gap-3">
                  <div className="flex gap-1.5 hidden sm:flex"><span className="w-2.5 h-2.5 rounded-full bg-rose-500/80"></span><span className="w-2.5 h-2.5 rounded-full bg-amber-500/80"></span><span className="w-2.5 h-2.5 rounded-full bg-emerald-500/80"></span></div>
                  <span className="text-[11px] font-mono text-slate-400 bg-slate-900 px-2 py-0.5 rounded border border-slate-800">{activeFile.path}</span>
                </div>
                <span className="text-[10px] font-mono text-slate-500 uppercase">{activeFile.language}</span>
              </div>
              <div className="flex-1 relative bg-[#000205]">
                <Editor
                  height="100%"
                  theme="vs-dark"
                  language={getMonacoLanguage(activeFile.language)}
                  value={activeFile.content}
                  options={{ readOnly: true, minimap: { enabled: false }, fontSize: 13, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace', padding: { top: 20, bottom: 20 }, scrollBeyondLastLine: false }}
                />
              </div>
            </div>
          )}

          {/* Tab 2: Dynamic Git-Style Side-by-Side Diff Viewer */}
          {activeTab === 'diff' && (
            <div className="flex-1 p-4 sm:p-6 bg-[#000205] overflow-y-auto font-mono text-xs space-y-4">
              <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-800 pb-3">
                <span className="text-slate-400 font-bold uppercase tracking-widest text-[10px]">
                  Compare HEAD vs Revision Snapshot ({activeFile.path})
                </span>
                
                {script.revisions && script.revisions.length > 0 && (
                  <div className="flex items-center gap-2">
                    <span className="text-slate-500 text-[10px]">Compare against:</span>
                    <select
                      value={selectedRevisionId}
                      onChange={(e) => setSelectedRevisionId(e.target.value)}
                      className="bg-slate-900 border border-slate-800 text-indigo-300 rounded px-2 py-1 text-xs focus:outline-none"
                    >
                      {script.revisions.map((rev: any, idx: number) => (
                        <option key={rev.id} value={rev.id}>
                          Revision #{script.revisions.length - idx} ({new Date(rev.createdAt).toLocaleDateString()})
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
              
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-2 border border-slate-800 rounded-xl overflow-hidden bg-slate-950">
                <div className="border-b lg:border-b-0 lg:border-r border-slate-800 p-3 overflow-x-auto bg-slate-950/50">
                  <div className="text-[10px] text-slate-500 pb-2 border-b border-slate-900 font-bold mb-2 flex items-center justify-between">
                    <span>Historical Snapshot</span>
                    <span className="text-slate-400">{activeFile.path}</span>
                  </div>
                  <pre className="text-[11px] text-slate-400 leading-relaxed font-mono whitespace-pre"><code>{historicalBaselineCode}</code></pre>
                </div>
                <div className="p-3 overflow-x-auto bg-emerald-950/10">
                  <div className="text-[10px] text-emerald-400 pb-2 border-b border-slate-900 font-bold mb-2 flex items-center justify-between">
                    <span>HEAD (Current Revision)</span>
                    <span className="text-emerald-400 font-mono">{activeFile.path}</span>
                  </div>
                  <pre className="text-[11px] text-emerald-300 leading-relaxed font-mono whitespace-pre"><code>{activeFile.content}</code></pre>
                </div>
              </div>
            </div>
          )}

          {/* Tab 3: Database Commit Ledger */}
          {activeTab === 'history' && (
            <div className="flex-1 p-6 sm:p-8 bg-[#000205] overflow-y-auto space-y-4 font-mono text-xs">
              <div className="text-slate-400 font-bold mb-2 uppercase tracking-widest text-[10px]">Revision Ledger</div>
              
              <div className="p-4 bg-slate-950 border border-indigo-500/30 rounded-xl flex items-center justify-between shadow-md">
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <span className="px-2 py-0.5 bg-indigo-950 text-indigo-400 rounded text-[10px] font-bold animate-pulse">HEAD</span>
                    <span className="text-white font-bold">Latest active release &bull; {commitHash}</span>
                  </div>
                  <div className="text-slate-500 text-[11px]">Author: @{script.author?.username || script.author?.name || 'author'}</div>
                </div>
                <span className="text-emerald-400 font-bold">{new Date(script.updatedAt).toLocaleDateString()}</span>
              </div>

              {script.revisions?.map((rev: any, index: number) => (
                <div key={rev.id} className="p-4 bg-slate-950/80 border border-slate-800 rounded-xl flex items-center justify-between">
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <span className="px-2 py-0.5 bg-slate-900 text-slate-400 rounded text-[10px] font-bold">REV-{script.revisions.length - index}</span>
                      <span className="text-slate-300 font-bold">Snapshot backup commit</span>
                    </div>
                    <div className="text-slate-500 text-[11px]">Saved automatically on update</div>
                  </div>
                  <span className="text-slate-400 font-bold">{new Date(rev.createdAt).toLocaleDateString()}</span>
                </div>
              ))}

              <div className="p-4 bg-slate-950 border border-slate-800 rounded-xl flex items-center justify-between">
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <span className="px-2 py-0.5 bg-slate-900 text-slate-500 rounded text-[10px] font-bold">INIT</span>
                    <span className="text-slate-400 font-bold">Initial configuration genesis</span>
                  </div>
                  <div className="text-slate-500 text-[11px]">Author: @{script.author?.username || script.author?.name || 'author'}</div>
                </div>
                <span className="text-slate-500 font-bold">{new Date(script.createdAt).toLocaleDateString()}</span>
              </div>
            </div>
          )}

          {/* Tab 4: Automated Linter Diagnostics */}
          {activeTab === 'linters' && (
            <div className="flex-1 p-6 sm:p-8 bg-[#000205] overflow-y-auto space-y-4 font-mono text-xs">
              <div className="text-slate-400 font-bold mb-2 uppercase tracking-widest text-[10px]">Automated Security &amp; Syntax Linters</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="p-4 bg-slate-950 border border-emerald-500/30 rounded-xl space-y-2">
                  <div className="flex items-center justify-between text-emerald-400 font-bold">
                    <span>✓ Syntax Linter ({activeFile.language})</span>
                    <span>PASSED</span>
                  </div>
                  <p className="text-slate-400 text-[11px] font-sans">AST linter verified 0 structural or parsing defects in active file schema.</p>
                </div>

                <div className="p-4 bg-slate-950 border border-indigo-500/30 rounded-xl space-y-2">
                  <div className="flex items-center justify-between text-indigo-400 font-bold">
                    <span>🔒 Secret Leak Scanner</span>
                    <span>SECURE</span>
                  </div>
                  <p className="text-slate-400 text-[11px] font-sans">No hardcoded API tokens, JWT secrets, or unencrypted database credentials found.</p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Discussion Section */}
      <section className="bg-slate-900/40 backdrop-blur-xl border border-slate-800/80 rounded-xl p-6 sm:p-10 shadow-2xl space-y-6">
        <h3 className="text-sm font-mono font-bold uppercase tracking-wider text-slate-300">
          Discussion & Technical Feedback ({script.comments.length})
        </h3>

        {currentUserId && (
          <form onSubmit={handleComment} className="space-y-3">
            <textarea
              rows={3}
              value={commentContent}
              onChange={(e) => setCommentContent(e.target.value)}
              placeholder="Contribute technical feedback, edge cases, or deployment tips..."
              className="w-full bg-[#020408] border border-slate-800 rounded-xl p-4 text-xs text-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500 font-mono shadow-inner"
            />
            <div className="flex justify-end">
              <button
                type="submit"
                disabled={submitting || !commentContent.trim()}
                className="bg-indigo-600 hover:bg-indigo-500 text-white font-extrabold px-6 py-2.5 rounded-xl text-xs transition-all shadow-lg shadow-indigo-600/30 disabled:opacity-50 cursor-pointer uppercase tracking-wider"
              >
                {submitting ? 'Transmitting...' : 'Post Technical Comment'}
              </button>
            </div>
          </form>
        )}

        <div className="space-y-4 pt-4">
          {script.comments.length === 0 ? (
            <p className="text-xs font-mono text-slate-500 py-4 text-center">No comments posted on this payload yet. Be the first to start the discussion.</p>
          ) : (
            script.comments.map((c: any) => (
              <div key={c.id} className="p-5 bg-[#020408] border border-slate-800 rounded-xl space-y-2 font-mono">
                <div className="flex items-center justify-between text-xs">
                  <span className="text-indigo-400 font-bold">@{c.author.username || c.author.name}</span>
                  <span className="text-slate-600">{new Date(c.createdAt).toLocaleTimeString()}</span>
                </div>
                <p className="text-slate-300 text-xs font-sans leading-relaxed">{c.content}</p>
              </div>
            ))
          )}
        </div>
      </section>

      {/* Forensic Audit Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-md animate-fade-in font-mono">
          <div className="relative w-full max-w-xl bg-[#070b14] border-2 border-indigo-500/50 rounded-xl p-6 sm:p-8 shadow-[0_0_50px_rgba(99,102,241,0.3)] space-y-6">
            
            <div className="flex items-center justify-between border-b border-slate-800 pb-4">
              <div className="space-y-1">
                <div className="text-[10px] text-indigo-400 uppercase tracking-widest font-bold">Neural Security Audit</div>
                <h3 className="text-lg font-black text-white tracking-tight">Craftsmanship Breakdown</h3>
              </div>
              <button 
                onClick={() => setIsModalOpen(false)}
                className="w-8 h-8 rounded-lg bg-slate-900 hover:bg-slate-800 text-slate-400 hover:text-white flex items-center justify-center border border-slate-800 transition-colors cursor-pointer"
              >
                ✕
              </button>
            </div>

            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-slate-950 border border-slate-800 rounded-xl">
                <div>
                  <div className="text-xs font-bold text-slate-300">Composite Score</div>
                  <div className="text-[11px] text-slate-500">Adversarial heuristic evaluation</div>
                </div>
                <div className={`text-xl font-black ${humanCraftScore >= 70 ? 'text-emerald-400' : 'text-amber-400'}`}>
                  {humanCraftScore}% Human ({aiScore}% AI)
                </div>
              </div>

              <div className="space-y-2">
                <div className="text-xs font-bold text-slate-400 uppercase tracking-wider">Heuristic Audit Log:</div>
                <div className="space-y-2 bg-slate-950/90 border border-slate-800/90 rounded-xl p-4 max-h-60 overflow-y-auto">
                  {breakdown.length === 0 ? (
                    <p className="text-xs text-slate-500">No anomalous AI traits or conversational filler blocks found in payload.</p>
                  ) : (
                    breakdown.map((item: string, idx: number) => (
                      <div key={idx} className="flex items-start gap-2.5 text-xs text-slate-300 py-1 border-b border-slate-900 last:border-0">
                        <span className="text-indigo-400 mt-0.5">&gt;</span>
                        <span>{item}</span>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>

            <div className="flex justify-end pt-2">
              <button
                onClick={() => setIsModalOpen(false)}
                className="bg-indigo-600 hover:bg-indigo-500 text-white font-bold px-6 py-2.5 rounded-xl text-xs transition-all shadow-lg shadow-indigo-600/30 cursor-pointer uppercase tracking-wider"
              >
                Close Audit Report
              </button>
            </div>

          </div>
        </div>
      )}

    </div>
  )
}